/**
 * Silent sign-in against the Hirax SSO (backend mode).
 *
 * The browser never holds tokens: a hidden iframe asks the SSO for a code with
 * `prompt=none`, and the code plus PKCE verifier are handed to the backend,
 * which exchanges them and sets HttpOnly cookies. The interactive login is a
 * plain redirect to the backend's /auth/login.
 */

// Must be the OIDC issuer the backend uses: /auth/login signs users in there,
// so that is where the SSO session cookie lives.
const AUTH_SERVER = 'https://auth.hirax.cloud'
const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID as string | undefined
const SCOPES = ['openid', 'profile', 'email']
const TIMEOUT_MS = 5000

export interface SilentAuthResult {
  code: string
  state: string
  codeVerifier: string
  redirectUri: string
  clientId: string
}

function randomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  const values = crypto.getRandomValues(new Uint8Array(length))
  return Array.from(values, (v) => charset[v % charset.length]).join('')
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  let str = ''
  new Uint8Array(digest).forEach((b) => (str += String.fromCharCode(b)))
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function trySilentAuth(): Promise<SilentAuthResult> {
  if (!CLIENT_ID) throw new Error('VITE_AUTH_CLIENT_ID is not configured')
  // If the SSO answers with a redirect, the iframe ends up on this app again.
  // Never start another silent sign-in from inside a frame, or they nest forever.
  if (isEmbedded()) throw new Error('Silent authentication is not run inside a frame')

  const redirectUri = `${window.location.origin}/auth/callback`
  const codeVerifier = randomString(128)
  const state = randomString(32)
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
    scope: SCOPES.join(' '),
    state,
    code_challenge: await challengeFor(codeVerifier),
    code_challenge_method: 'S256',
    prompt: 'none',
    response_mode: 'web_message',
  })

  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.title = 'Sign-in check'
  iframe.src = `${AUTH_SERVER}/lnauth/auth?${params}`

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      iframe.remove()
    }
    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('Silent authentication timed out'))
    }, TIMEOUT_MS)

    function onMessage(event: MessageEvent) {
      if (event.origin !== AUTH_SERVER) return
      const data = event.data as { type?: string; code?: string; state?: string; error?: string } | null
      if (!data || data.type !== 'oauth-callback') return
      cleanup()
      if (data.error || !data.code) return reject(new Error(data.error ?? 'No code returned'))
      // Origin is already pinned to the SSO; reject only a state that is present and wrong.
      if (data.state && data.state !== state) return reject(new Error('State mismatch'))
      resolve({ code: data.code, state, codeVerifier, redirectUri, clientId: CLIENT_ID! })
    }

    window.addEventListener('message', onMessage)

    // Fallback when the SSO redirects instead of posting a message: once the
    // frame is back on our origin, stop it before the app boots inside it.
    iframe.onload = () => {
      let href: string
      try {
        href = iframe.contentWindow?.location.href ?? ''
      } catch {
        return // Still on the SSO (cross-origin) – wait for the message or the timeout.
      }
      if (!href || href === 'about:blank') return
      cleanup()
      const params = new URL(href).searchParams
      const code = params.get('code')
      if (!code) return reject(new Error(params.get('error') ?? 'No code returned'))
      if (params.get('state') && params.get('state') !== state) return reject(new Error('State mismatch'))
      resolve({ code, state, codeVerifier, redirectUri, clientId: CLIENT_ID! })
    }

    document.body.appendChild(iframe)
  })
}

export function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

const RETURN_KEY = 'kagami:return-to'

/** Full-page redirect into the backend's OAuth flow; we come back to `/`. */
export function startLogin() {
  try {
    sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search)
  } catch {
    // ignore
  }
  window.location.href = '/auth/login'
}

/** The page the user was on before signing in, consumed once. */
export function takeReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_KEY)
    sessionStorage.removeItem(RETURN_KEY)
    // Only same-origin, absolute paths – never a protocol-relative URL.
    return path && path.startsWith('/') && !path.startsWith('//') ? path : null
  } catch {
    return null
  }
}

export function logout() {
  window.location.href = '/auth/logout'
}
